import React, { useState, useEffect } from "react";
import Layout from "../../components/layout/Layout";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { itemCategoriesApi } from "../../api/itemCategories";
import { uploadApi } from "../../api/upload";
import Swal from "sweetalert2";

interface Category {
  _id: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  poster?: string;
  iconUrl?: string;
  tagline1?: string;
  tagline2?: string;
}

const ItemCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    displayOrder: 0,
    isActive: true,
    poster: "",
    iconUrl: "",
    tagline1: "",
    tagline2: "",
  });

  const handlePosterUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploadingPoster(true);
    try {
      const response = await uploadApi.uploadImage(file, "categories/posters");
      setFormData((prev) => ({ ...prev, poster: response.imageUrl }));
      Swal.fire("Uploaded", "Poster uploaded successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to upload poster", "error");
    } finally {
      setUploadingPoster(false);
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploadingIcon(true);
    try {
      const response = await uploadApi.uploadImage(file, "categories/icons");
      setFormData((prev) => ({ ...prev, iconUrl: response.imageUrl }));
      Swal.fire("Uploaded", "Icon uploaded successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to upload icon", "error");
    } finally {
      setUploadingIcon(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await itemCategoriesApi.getCategories();
      setCategories(data.categories || []);
    } catch (error) {
      console.error("Failed to fetch categories", error);
      Swal.fire("Error", "Could not fetch categories", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleOpenModal = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        displayOrder: category.displayOrder,
        isActive: category.isActive,
        poster: category.poster || "",
        iconUrl: category.iconUrl || "",
        tagline1: category.tagline1 || "",
        tagline2: category.tagline2 || "",
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: "",
        displayOrder: 0,
        isActive: true,
        poster: "",
        iconUrl: "",
        tagline1: "",
        tagline2: "",
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await itemCategoriesApi.updateCategory(editingCategory._id, formData);
        Swal.fire("Success", "Category updated", "success");
      } else {
        await itemCategoriesApi.createCategory(formData);
        Swal.fire("Success", "Category created", "success");
      }
      handleCloseModal();
      fetchCategories();
    } catch (error) {
      Swal.fire("Error", "Failed to save category", "error");
    }
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await itemCategoriesApi.deleteCategory(id);
        Swal.fire("Deleted!", "Category has been deleted.", "success");
        fetchCategories();
      } catch (error) {
        Swal.fire("Error", "Failed to delete category", "error");
      }
    }
  };

  return (
    <Layout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Item Categories
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          sx={{ borderRadius: 2 }}
        >
          Add Category
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Display Order</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((row) => (
                    <TableRow key={row._id} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.displayOrder}</TableCell>
                      <TableCell>
                        <Switch
                          checked={row.isActive}
                          color="success"
                          onChange={async () => {
                            try {
                              await itemCategoriesApi.updateCategory(row._id, {
                                ...row,
                                isActive: !row.isActive,
                              });
                              fetchCategories();
                            } catch (err) {
                              Swal.fire("Error", "Could not toggle status", "error");
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenModal(row)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(row._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {categories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        No categories found. Create one!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingCategory ? "Edit Category" : "Add Category"}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                label="Display Order"
                type="number"
                fullWidth
                value={formData.displayOrder}
                onChange={(e) =>
                  setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })
                }
              />
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  label="Poster Image URL"
                  fullWidth
                  value={formData.poster}
                  onChange={(e) => setFormData({ ...formData, poster: e.target.value })}
                  placeholder="e.g. https://domain.com/poster.png"
                />
                <Button variant="contained" component="label" disabled={uploadingPoster} sx={{ minWidth: 100, height: 56 }}>
                  {uploadingPoster ? <CircularProgress size={24} color="inherit" /> : "Upload"}
                  <input type="file" hidden accept="image/*" onChange={handlePosterUpload} />
                </Button>
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  label="Fallback Icon URL"
                  fullWidth
                  value={formData.iconUrl}
                  onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
                  placeholder="e.g. https://domain.com/icon.png"
                />
                <Button variant="contained" component="label" disabled={uploadingIcon} sx={{ minWidth: 100, height: 56 }}>
                  {uploadingIcon ? <CircularProgress size={24} color="inherit" /> : "Upload"}
                  <input type="file" hidden accept="image/*" onChange={handleIconUpload} />
                </Button>
              </Box>
              <TextField
                label="Custom Tagline Line 1 (Optional)"
                fullWidth
                value={formData.tagline1}
                onChange={(e) => setFormData({ ...formData, tagline1: e.target.value })}
                placeholder="e.g. Real Blends,"
              />
              <TextField
                label="Custom Tagline Line 2 (Optional)"
                fullWidth
                value={formData.tagline2}
                onChange={(e) => setFormData({ ...formData, tagline2: e.target.value })}
                placeholder="e.g. Filtered for you."
              />
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>Active Status:</Typography>
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
    </Layout>
  );
};

export default ItemCategories;
